import { Type } from 'class-transformer';
import {
  ArrayMinSize,
  IsArray,
  IsBoolean,
  IsDateString,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { TransportDocumentItemDto } from './transport-document-item.dto';

export const TRANSPORT_MODES = ['MITTENTE', 'VETTORE', 'DESTINATARIO'] as const;
export type TransportModeValue = (typeof TRANSPORT_MODES)[number];

/** Freezer-grade lower bound; upper bound covers any hot transport. */
export const TRANSPORT_TEMPERATURE_MIN_C = -80;
export const TRANSPORT_TEMPERATURE_MAX_C = 200;

/**
 * Declared transport temperature (HACCP / D.P.R. 472/96): either "ambient"
 * (`transportTemperatureAmbient: true`) or a min–max range in °C. Null fields
 * = not declared.
 */
export class TransportTemperatureDto {
  @IsBoolean()
  @IsOptional()
  transportTemperatureAmbient?: boolean | null;

  @IsInt()
  @Min(TRANSPORT_TEMPERATURE_MIN_C)
  @Max(TRANSPORT_TEMPERATURE_MAX_C)
  @IsOptional()
  transportTemperatureMinC?: number | null;

  @IsInt()
  @Min(TRANSPORT_TEMPERATURE_MIN_C)
  @Max(TRANSPORT_TEMPERATURE_MAX_C)
  @IsOptional()
  transportTemperatureMaxC?: number | null;
}

/** Range mode requires both bounds in the right order; ambient excludes a range. */
export function assertValidTransportTemperature(dto: TransportTemperatureDto): void {
  if (dto.transportTemperatureAmbient) {
    return;
  }
  const { transportTemperatureMinC: minC, transportTemperatureMaxC: maxC } = dto;
  if ((minC != null || maxC != null) && (minC == null || maxC == null || minC > maxC)) {
    throw new Error('temperatureRangeInvalid');
  }
}

export class CreateTransportDocumentDto
  extends TransportTemperatureDto
  implements TransportTemperatureDto
{
  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  customerId?: string;

  @IsString()
  @IsNotEmpty()
  causale!: string;

  @IsIn(TRANSPORT_MODES)
  transportMode!: TransportModeValue;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  carrierName?: string;

  @IsInt()
  @IsOptional()
  @Min(1)
  packagesCount?: number;

  @IsNumber()
  @IsOptional()
  grossWeightKg?: number;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  goodsAppearance?: string;

  @IsDateString()
  @IsNotEmpty()
  transportStartedAt!: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  notes?: string;

  @IsString()
  @IsOptional()
  @OptionalStringTransform()
  destinationAddress?: string;

  @IsArray()
  @ArrayMinSize(1)
  @ValidateNested({ each: true })
  @Type(() => TransportDocumentItemDto)
  items!: TransportDocumentItemDto[];
}
